import { useEffect, useRef, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PackagePlus, Search, FileDown } from "lucide-react";
import { exportProductsToXLSX } from "@/lib/exportProducts";

export default function Inventory() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");                               // search

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", delta_qty: "", reason: "", type: "purchase" });
  const [saving, setSaving] = useState(false);

  const fetchRows = async (pg = page) => {
    setLoading(true);
    try {
      const url = tab === "low" ? `/inventory/low-stock?limit=${limit}` : `/inventory?page=${pg}&limit=${limit}`;
      const { data } = await api.get(url);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(page); /* eslint-disable-next-line */ }, [page, tab]);

  useEffect(() => {
    api.get("/products?limit=100").then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, []);

  // Client-side filter — searches product name + sku
  const productById = (id) => products.find((p) => p.id === id);
  const filtered = q.trim()
    ? rows.filter((r) => {
        const p = productById(r.product_id);
        const hay = `${r.product_name || p?.name || ""} ${r.sku || p?.sku || ""} ${p?.barcode || ""}`.toLowerCase();
        return hay.includes(q.trim().toLowerCase());
      })
    : rows;

  const doAdjust = async () => {
    setSaving(true);
    try {
      await api.post("/inventory/adjust", {
        product_id: form.product_id,
        delta_qty: Number(form.delta_qty),
        reason: form.reason || `Manual ${form.type}`,
        type: form.type,
      });
      toast.success("Stock adjusted");
      setAdjustOpen(false);
      setForm({ product_id: "", delta_qty: "", reason: "", type: "purchase" });
      fetchRows(page);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const productName = (id) => products.find((p) => p.id === id)?.name || id;

  const columns = [
    { key: "product_id", header: "Product", render: (r) => (
      <div>
        <div className="font-medium truncate">{r.product_name || productName(r.product_id)}</div>
        <div className="text-xs text-muted-foreground">{r.sku || r.product_id?.slice(-6)}</div>
      </div>
    )},
    { key: "qty_on_hand", header: "On hand", className: "w-24 text-right",
      render: (r) => <span className="font-mono font-semibold">{r.qty_on_hand ?? 0}</span> },
    { key: "qty_reserved", header: "Reserved", className: "w-24 text-right",
      render: (r) => <span className="font-mono text-muted-foreground">{r.qty_reserved ?? 0}</span> },
    { key: "qty_available", header: "Available", className: "w-24 text-right",
      render: (r) => <span className="font-mono font-semibold text-accent">{r.qty_available ?? (r.qty_on_hand - r.qty_reserved)}</span> },
    { key: "reorder_level", header: "Reorder @", className: "w-24 text-right",
      render: (r) => <span className={r.qty_on_hand < r.reorder_level ? "text-destructive font-semibold" : ""}>{r.reorder_level ?? 0}</span> },
    { key: "last_restocked_at", header: "Last restock", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.last_restocked_at)}</span> },
    { key: "status", header: "", className: "w-24",
      render: (r) => (r.qty_on_hand ?? 0) <= (r.reorder_level ?? 0)
        ? <Badge variant="destructive">Low</Badge>
        : <Badge variant="secondary">OK</Badge> },
  ];

  // Sprint — Bulk Product Export (.xlsx). Same helper as Products page — backend
  // scopes by X-Store-Id so SUPER_ADMIN sees all stores; STORE_ADMIN sees only theirs.
  const [exporting, setExporting] = useState(false);
  const runExport = async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Preparing Excel export…");
    try {
      const count = await exportProductsToXLSX();
      toast.dismiss(t);
      toast.success(`Exported ${count} product${count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(errMsg(e, "Export failed"));
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="inventory-page">
      <PageHeader
        title="Inventory"
        subtitle="Live on-hand + reservations"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={runExport}
              disabled={exporting}
              data-testid="inventory-export-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
            <Button onClick={() => setAdjustOpen(true)} className="btn-primary-glow" data-testid="adjust-stock-btn">
              <PackagePlus className="w-4 h-4 mr-2" />Adjust stock
            </Button>
          </div>
        }
      />

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by product name, SKU or barcode…"
            className="pl-9 h-10"
            data-testid="inventory-search"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} shown{q ? ` · matching "${q}"` : ""}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-inventory">All items</TabsTrigger>
          <TabsTrigger value="low" data-testid="tab-low-stock">Low stock</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <DataTable columns={columns} rows={filtered} loading={loading}
            {...(tab === "all" && !q ? { page, limit, total, onPage: setPage } : {})}
            testId="inventory-table" />
        </TabsContent>
      </Tabs>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Adjust stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product <span className="text-primary">*</span></Label>
              <SearchableProductPicker
                products={products}
                value={form.product_id}
                onChange={(id) => setForm({ ...form, product_id: id })}
              />
            </div>
            <div>
              <Label>Movement type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">Purchase / receiving</SelectItem>
                  <SelectItem value="adjustment">Manual adjustment</SelectItem>
                  <SelectItem value="damage">Damage / write-off</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Delta quantity (+/-) <span className="text-primary">*</span></Label>
              <Input type="number" value={form.delta_qty} onChange={(e) => setForm({ ...form, delta_qty: e.target.value })} placeholder="+50 or -3" data-testid="adjust-qty" />
            </div>
            <div><Label>Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="adjust-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button onClick={doAdjust} disabled={saving || !form.product_id || !form.delta_qty} className="btn-primary-glow" data-testid="save-adjust-btn">
              {saving ? "Adjusting…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sprint 18 — inline searchable product picker. Filters TEST_* products out and
// gives a proper search-as-you-type experience for large catalogues.
function SearchableProductPicker({ products, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const clean = products.filter((p) => !/^TEST[_-]/i.test(p.name || "") && !/^S1[5-9]-/i.test(p.name || ""));
  const term = q.trim().toLowerCase();
  const filtered = term
    ? clean.filter((p) => `${p.name || ""} ${p.sku || ""} ${p.barcode || ""}`.toLowerCase().includes(term))
    : clean;
  const selected = products.find((p) => p.id === value);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-md border px-3 h-10 text-sm bg-card hover:bg-secondary/40 transition text-left"
        data-testid="adjust-product-trigger"
      >
        <span className={selected ? "truncate" : "text-muted-foreground"}>
          {selected ? selected.name : "Select product…"}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-card shadow-lg max-h-72 flex flex-col" data-testid="adjust-product-dropdown">
          <div className="p-2 border-b sticky top-0 bg-card">
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, SKU, barcode…"
              className="h-8 text-sm"
              data-testid="adjust-product-search"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground text-center">No products match &quot;{q}&quot;</div>}
            {filtered.slice(0, 100).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition flex items-center gap-2 ${p.id === value ? "bg-primary/5 text-primary font-medium" : ""}`}
                data-testid={`adjust-product-opt-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate">{p.name}</div>
                  {p.sku && <div className="text-[10px] text-muted-foreground font-mono truncate">{p.sku}</div>}
                </div>
                {typeof p.selling_price === "number" && <div className="text-xs text-muted-foreground shrink-0">₹{p.selling_price}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

